import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/api";
import { fmtMoney, fmtPct, StatusPill, AgingChip, ALL_STATUSES } from "../components/ops/opsUtils";
import { ArrowLeft, FileText, Activity, History, Receipt, Edit3, Save, Plus, TrendingDown, Layers as LayersIcon, ListChecks, FileSignature, Trash2, RotateCcw, Mail, MessageSquare, Wallet } from "lucide-react";
import CommunicationsTab from "../components/communications/CommunicationsTab";
import ActivityTimeline from "../components/communications/ActivityTimeline";
import SendCustomEmailDialog from "../components/communications/SendCustomEmailDialog";
import LoanActionRail from "../components/communications/LoanActionRail";
import AgreementWorkflow from "../components/agreements/AgreementWorkflow";
import LoanCapitalEconomics from "../components/LoanCapitalEconomics";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import GeneratePayoffModal from "../components/payoffs/GeneratePayoffModal";
import CreateTaskModal from "../components/tasks/CreateTaskModal";
import EditLoanModal from "../components/admin/EditLoanModal";
import { toast } from "sonner";

export default function LoanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [tab, setTab] = useState("overview");
  const [newStatus, setNewStatus] = useState("");
  const [settled, setSettled] = useState("");
  const [settlementDate, setSettlementDate] = useState("");
  const [showEditSettlementDate, setShowEditSettlementDate] = useState(false);
  const [note, setNote] = useState("");
  const [showPayoff, setShowPayoff] = useState(false);
  const [showTask, setShowTask] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const generateAgreement = () => {
    // Jump to the Agreement tab where the new AgreementWorkflow drives
    // the entire generate → review → send flow — no legacy contracts
    // endpoint, no JSON, no template-admin round-trips.
    setTab("agreement");
    setTimeout(() => {
      const el = document.querySelector('[data-testid="agreement-workflow"]')
              || document.querySelector('[data-testid="agreement-workflow-loading"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };
  const [advDraft, setAdvDraft] = useState({ amount: "", advance_date: new Date().toISOString().slice(0, 10), monthly_rate: "", note: "" });
  const [pdDraft, setPdDraft] = useState({ amount: "", paydown_date: new Date().toISOString().slice(0, 10), kind: "paydown", note: "" });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const load = async () => {
    try {
      const { data } = await api.get(`/operations/loans/${id}`);
      setLoan(data);
      setNewStatus(data.current_status);
      setSettled(data.settled_amount ? String(data.settled_amount) : "");
      setSettlementDate((data.settlement_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [notifyPlaintiff, setNotifyPlaintiff] = useState(true);
  const [notifyAttorney, setNotifyAttorney] = useState(true);
  const [notifyInternal, setNotifyInternal] = useState(true);
  const [showCustomEmail, setShowCustomEmail] = useState(false);

  const saveStatus = async () => {
    const closing = newStatus === "Settled" || newStatus === "Closed";
    if (closing && !settlementDate) {
      toast.error("Settlement Date is required when marking a loan Settled or Closed.");
      return;
    }
    if (closing && !window.confirm(`Confirm Settlement Date: ${settlementDate}\n\nThis is the actual date the case settled (business date). It is separate from the status-change timestamp and can be edited later from the loan detail.`)) {
      return;
    }
    try {
      await api.patch(`/operations/loans/${id}/status`, {
        new_status: newStatus,
        settled_amount: settled ? Number(settled) : null,
        settlement_date: closing ? settlementDate : null,
        notify_plaintiff: notifyPlaintiff,
        notify_attorney: notifyAttorney,
        notify_internal: notifyInternal,
      });
      const notifyMsg = [notifyPlaintiff && "plaintiff", notifyAttorney && "attorney", notifyInternal && "team"].filter(Boolean).join(", ");
      toast.success(`Status → ${newStatus}${notifyMsg ? ` · notified: ${notifyMsg}` : " · silent"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/operations/loans/${id}/notes`, { content: note.trim() });
    setNote(""); load(); toast.success("Note added");
  };

  const softDelete = async () => {
    const reason = window.prompt(`Soft-delete loan ${loan?.loan_id} for ${loan?.client_name}?\n\nIt will be hidden everywhere but recoverable from the Archived view. Optionally enter a reason:`);
    if (reason === null) return;
    try {
      await api.delete(`/operations/loans/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`);
      toast.success("Loan archived");
      navigate("/admin/loans");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async () => {
    try {
      await api.post(`/operations/loans/${id}/restore`);
      toast.success("Loan restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addAdvance = async () => {
    if (!advDraft.amount) return;
    try {
      await api.post(`/operations/loans/${id}/advances`, {
        amount: Number(advDraft.amount),
        advance_date: new Date(advDraft.advance_date).toISOString(),
        monthly_rate: advDraft.monthly_rate ? Number(advDraft.monthly_rate) : null,
        note: advDraft.note,
      });
      setAdvDraft({ amount: "", advance_date: new Date().toISOString().slice(0, 10), monthly_rate: "", note: "" });
      toast.success("Advance recorded"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addPaydown = async () => {
    if (!pdDraft.amount) return;
    try {
      await api.post(`/operations/loans/${id}/paydowns`, {
        amount: Number(pdDraft.amount),
        paydown_date: new Date(pdDraft.paydown_date).toISOString(),
        kind: pdDraft.kind,
        note: pdDraft.note,
      });
      setPdDraft({ amount: "", paydown_date: new Date().toISOString().slice(0, 10), kind: "paydown", note: "" });
      toast.success(`${pdDraft.kind === "reduction" ? "Reduction" : "Paydown"} recorded`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!loan) return <div className="p-10 text-[#6B7280]" data-testid="loan-detail-loading">Loading loan…</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="loan-detail">
      <Link to="/admin/loans" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0A2540] mb-3" data-testid="back-to-loans">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All loans
      </Link>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div>
          {/* Header card */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{loan.loan_id}</div>
                <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">{loan.client_name}</h1>
                <div className="text-sm text-[#6B7280] mt-1.5">
                  <Link to={`/admin/attorneys/${encodeURIComponent(loan.attorney_email)}`} className="hover:text-[#0066FF]">{loan.attorney_name || loan.attorney_email}</Link>
                  {loan.attorney_firm ? ` · ${loan.attorney_firm}` : ""} · {loan.case_type}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusPill status={loan.current_status} size="lg" />
                <AgingChip bucket={loan.aging_bucket} />
                <div className="flex gap-1.5 mt-1">
                  <button onClick={() => setShowPayoff(true)} data-testid="generate-payoff-from-loan"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs font-medium transition-colors">
                    <Receipt className="w-3.5 h-3.5" /> Payoff
                  </button>
                  <button onClick={generateAgreement} data-testid="generate-agreement-from-loan"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[#0A2540] hover:bg-[#1a3556] text-white text-xs font-medium transition-colors">
                    <FileSignature className="w-3.5 h-3.5" /> Agreement
                  </button>
                  <button onClick={() => setShowTask(true)} data-testid="add-task-from-loan"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#374151] text-xs font-medium transition-colors">
                    <ListChecks className="w-3.5 h-3.5" /> Task
                  </button>
                  <button onClick={() => setShowEdit(true)} data-testid="edit-loan-btn"
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-white border border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF] text-[#374151] text-xs font-medium transition-colors">
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </button>
                  {loan.is_deleted ? (
                    <button onClick={restore} data-testid="restore-loan-btn"
                      className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-[#ECFDF5] border border-[#A7F3D0] hover:bg-[#D1FAE5] text-[#047857] text-xs font-medium transition-colors">
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                  ) : (
                    <button onClick={softDelete} data-testid="delete-loan-btn"
                      className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-white border border-[#E5E7EB] hover:border-[#B91C1C] hover:text-[#B91C1C] text-[#6B7280] text-xs font-medium transition-colors">
                      <Trash2 className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-6 pt-5 border-t border-[#F3F4F6]">
              <Stat label="Principal" value={fmtMoney(loan.principal)} />
              <Stat label="Current payoff" value={fmtMoney(loan.current_payoff)} accent />
              <Stat label="Accrued interest" value={fmtMoney(loan.accrued_interest)} />
              <Stat label="Rate" value={`${(loan.monthly_rate * 100).toFixed(2)}%/mo`} />
              <Stat label="Funded on" value={formatDate(loan.funding_date)} />
              <Stat label="Tiers elapsed" value={`${loan.tiers_elapsed} of ${loan.max_tiers}`} />
              <Stat label="Months out" value={`${loan.months_outstanding?.toFixed(1) || 0}`} />
              {loan.settlement_date && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1 flex items-center gap-1.5">
                    Settlement date
                    <button
                      onClick={() => { setSettlementDate(loan.settlement_date.slice(0,10)); setShowEditSettlementDate(true); }}
                      data-testid="edit-settlement-date-btn"
                      className="text-[#0066FF] hover:text-[#0052CC]"
                      title="Edit settlement date">
                      <Edit3 className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="tabular-nums text-[#0A2540] font-medium text-lg" data-testid="loan-settlement-date">
                    {formatDate(loan.settlement_date)}
                  </div>
                </div>
              )}
              {loan.status_changed_at && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Status changed</div>
                  <div className="tabular-nums text-[#0A2540] font-medium text-sm" data-testid="loan-status-changed-at">
                    {formatDate(loan.status_changed_at)}
                    {loan.status_changed_by && <span className="text-[#6B7280] text-xs block">by {loan.status_changed_by}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg">
            <div className="border-b border-[#E5E7EB] flex">
              {[
                ["overview", "Overview", FileText],
                ["agreement", "Agreement", FileSignature],
                ["schedule", "Schedule", Receipt],
                ["advances", "Advances", LayersIcon],
                ["paydowns", "Paydowns", TrendingDown],
                ["capital", "Capital", Wallet],
                ["notes", "Notes", Edit3],
                ["comms", "Emails", Mail],
                ["timeline", "Timeline", MessageSquare],
                ["audit", "Audit trail", History],
              ].map(([k, l, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`px-5 h-11 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors ${
                    tab === k ? "border-[#0066FF] text-[#0A2540]" : "border-transparent text-[#6B7280] hover:text-[#0A2540]"
                  }`}>
                  <Icon className="w-3.5 h-3.5" /> {l}
                </button>
              ))}
            </div>

            <div className="p-5 min-h-[260px]">
              {tab === "overview" && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Detail label="Client email" value={loan.client_email || "—"} />
                  <Detail label="Client phone" value={loan.client_phone || "—"} />
                  <Detail label="Attorney email" value={loan.attorney_email} />
                  <Detail label="Attorney firm" value={loan.attorney_firm || "—"} />
                  <Detail label="Case type" value={loan.case_type} />
                  <Detail label="Case state" value={loan.case_state || "—"} />
                  <Detail label="Contract status" value={loan.contract_status || "—"} />
                  <Detail label="DocuSign sent" value={loan.docusign_sent ? "Yes" : "No"} />
                </div>
              )}
              {tab === "agreement" && (
                <AgreementWorkflow loanId={id} loan={loan} />
              )}
              {tab === "capital" && (
                <LoanCapitalEconomics loanId={id} />
              )}
              {tab === "schedule" && (
                <table className="w-full text-sm">
                  <thead className="border-b border-[#E5E7EB]">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                      <th className="py-2 font-medium">Tier</th>
                      <th className="py-2 font-medium">Month</th>
                      <th className="py-2 font-medium">Tier starts</th>
                      <th className="py-2 font-medium text-right">Payoff if settled</th>
                      <th className="py-2 font-medium text-right">Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.schedule.map((s) => (
                      <tr key={s.tier} data-testid={`schedule-row-${s.tier}`}
                          className={`border-b border-[#F3F4F6] ${s.tier === loan.tiers_elapsed ? "bg-[#F0F6FF]" : ""}`}>
                        <td className="py-2.5 text-[#0A2540] font-medium">Tier {s.tier}</td>
                        <td className="py-2.5 text-[#6B7280]">Month {s.month}</td>
                        <td className="py-2.5 text-[#6B7280] tabular-nums">{formatDate(s.tier_starts_on)}</td>
                        <td className="py-2.5 text-right text-[#0A2540] tabular-nums">{fmtMoney(s.payoff_if_settled_here)}</td>
                        <td className="py-2.5 text-right text-[#047857] tabular-nums">{fmtMoney(s.fee)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === "advances" && (
                <div className="space-y-4">
                  <div className="rounded-md border border-[#E5E7EB] p-4 bg-[#F9FAFB]">
                    <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2.5">Record an additional advance</div>
                    <div className="grid sm:grid-cols-4 gap-2">
                      <Input type="number" placeholder="Amount" value={advDraft.amount} onChange={(e) => setAdvDraft((d) => ({ ...d, amount: e.target.value }))} data-testid="adv-amount" />
                      <Input type="date" value={advDraft.advance_date} onChange={(e) => setAdvDraft((d) => ({ ...d, advance_date: e.target.value }))} data-testid="adv-date" />
                      <Input type="number" step="0.001" placeholder={`Rate (default ${(loan.monthly_rate * 100).toFixed(2)}%)`} value={advDraft.monthly_rate} onChange={(e) => setAdvDraft((d) => ({ ...d, monthly_rate: e.target.value }))} data-testid="adv-rate" />
                      <Button onClick={addAdvance} className="bg-[#0066FF] hover:bg-[#0052CC] h-10" data-testid="add-advance-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add advance</Button>
                    </div>
                    <Input className="mt-2" placeholder="Optional note" value={advDraft.note} onChange={(e) => setAdvDraft((d) => ({ ...d, note: e.target.value }))} data-testid="adv-note" />
                  </div>
                  {(loan.additional_advances || []).length === 0 ? (
                    <div className="text-sm text-[#6B7280] italic py-3">No additional advances yet. The principal above is the only one.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
                        <th className="py-2 font-medium">Date</th><th className="py-2 font-medium">Amount</th><th className="py-2 font-medium">Rate</th><th className="py-2 font-medium">Note</th><th className="py-2 font-medium text-right">Added by</th>
                      </tr></thead>
                      <tbody>
                        {loan.additional_advances.map((a) => (
                          <tr key={a.id} data-testid={`advance-row-${a.id}`} className="border-b border-[#F3F4F6]">
                            <td className="py-2.5 text-[#6B7280] tabular-nums">{formatDate(a.advance_date)}</td>
                            <td className="py-2.5 text-[#0A2540] font-medium tabular-nums">{fmtMoney(a.amount)}</td>
                            <td className="py-2.5 text-[#6B7280] tabular-nums">{(a.monthly_rate * 100).toFixed(2)}%</td>
                            <td className="py-2.5 text-[#374151]">{a.note || "—"}</td>
                            <td className="py-2.5 text-right text-[11px] text-[#9CA3AF]">{a.added_by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {tab === "paydowns" && (
                <div className="space-y-4">
                  <div className="rounded-md border border-[#E5E7EB] p-4 bg-[#F9FAFB]">
                    <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2.5">Record a paydown or negotiated reduction</div>
                    <div className="grid sm:grid-cols-4 gap-2">
                      <Input type="number" placeholder="Amount" value={pdDraft.amount} onChange={(e) => setPdDraft((d) => ({ ...d, amount: e.target.value }))} data-testid="pd-amount" />
                      <Input type="date" value={pdDraft.paydown_date} onChange={(e) => setPdDraft((d) => ({ ...d, paydown_date: e.target.value }))} data-testid="pd-date" />
                      <Select value={pdDraft.kind} onValueChange={(v) => setPdDraft((d) => ({ ...d, kind: v }))}>
                        <SelectTrigger data-testid="pd-kind"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paydown">Paydown (client payment)</SelectItem>
                          <SelectItem value="reduction">Negotiated reduction</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={addPaydown} className="bg-[#0066FF] hover:bg-[#0052CC] h-10" data-testid="add-paydown-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Record</Button>
                    </div>
                    <Input className="mt-2" placeholder="Optional note" value={pdDraft.note} onChange={(e) => setPdDraft((d) => ({ ...d, note: e.target.value }))} data-testid="pd-note" />
                  </div>
                  {(loan.paydowns || []).length === 0 ? (
                    <div className="text-sm text-[#6B7280] italic py-3">No paydowns or reductions yet.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
                        <th className="py-2 font-medium">Date</th><th className="py-2 font-medium">Type</th><th className="py-2 font-medium">Amount</th><th className="py-2 font-medium">Note</th><th className="py-2 font-medium text-right">Recorded by</th>
                      </tr></thead>
                      <tbody>
                        {loan.paydowns.map((p) => (
                          <tr key={p.id} data-testid={`paydown-row-${p.id}`} className="border-b border-[#F3F4F6]">
                            <td className="py-2.5 text-[#6B7280] tabular-nums">{formatDate(p.paydown_date)}</td>
                            <td className="py-2.5 text-[#374151] capitalize">{p.kind}</td>
                            <td className="py-2.5 text-[#047857] font-medium tabular-nums">−{fmtMoney(p.amount)}</td>
                            <td className="py-2.5 text-[#374151]">{p.note || "—"}</td>
                            <td className="py-2.5 text-right text-[11px] text-[#9CA3AF]">{p.added_by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {tab === "notes" && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Textarea rows={2} placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" />
                    <Button onClick={addNote} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="add-note-btn">Add</Button>
                  </div>
                  {(loan.notes || []).length === 0 && <div className="text-sm text-[#6B7280] italic">No notes yet.</div>}
                  {(loan.notes || []).slice().reverse().map((n) => (
                    <div key={n.id} className="p-3 rounded-md bg-[#F9FAFB] border border-[#E5E7EB]" data-testid={`note-${n.id}`}>
                      <div className="text-xs text-[#6B7280] flex justify-between"><span className="font-medium text-[#0A2540]">{n.author}</span><span>{formatDate(n.created_at)}</span></div>
                      <div className="text-sm text-[#374151] mt-1 whitespace-pre-wrap">{n.content}</div>
                    </div>
                  ))}
                </div>
              )}
              {tab === "comms" && <CommunicationsTab loanId={loan.loan_id} attorneyEmail={loan.attorney_email} />}
              {tab === "timeline" && <ActivityTimeline loanId={loan.loan_id} attorneyEmail={loan.attorney_email} entityType="loan" />}
              {tab === "audit" && (
                <div className="space-y-2">
                  {(loan.audit || []).length === 0 && <div className="text-sm text-[#6B7280] italic">No events yet.</div>}
                  {(loan.audit || []).map((e, i) => {
                    const isEditSD = e.event_type === "settlement_date_edit";
                    const isBackfillSD = e.event_type === "settlement_date_backfill";
                    return (
                      <div key={`audit-${e.event_id || e.created_at || i}`} className="text-sm py-2 border-b border-[#F3F4F6] last:border-b-0" data-testid={`audit-row-${i}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-[#0A2540] capitalize">{e.event_type.replace(/_/g, " ")}</span>
                            {e.payload?.from && e.payload?.to && <span className="ml-2 text-[#6B7280]">{e.payload.from} → <span className="text-[#0A2540]">{e.payload.to}</span></span>}
                            {isEditSD && (
                              <span className="ml-2 text-[#6B7280] tabular-nums" data-testid={`audit-sd-change-${i}`}>
                                {e.payload?.previous_settlement_date || "—"} → <span className="text-[#0A2540]">{e.payload?.new_settlement_date}</span>
                              </span>
                            )}
                            {isBackfillSD && (
                              <span className="ml-2 text-[#6B7280] tabular-nums">
                                → <span className="text-[#0A2540]">{e.payload?.new_settlement_date}</span> (from {e.payload?.source})
                              </span>
                            )}
                            <span className="ml-2 text-xs text-[#9CA3AF]">by {e.by_email || "system"}</span>
                          </div>
                          <span className="text-xs text-[#6B7280] tabular-nums">{formatDate(e.created_at)}</span>
                        </div>
                        {isEditSD && e.payload?.reason && (
                          <div className="mt-1 text-xs text-[#374151] italic pl-1">Reason: {e.payload.reason}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right rail */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Update status</div>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="mb-2" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {(newStatus === "Settled" || newStatus === "Closed") && (
              <div className="mb-2 space-y-2">
                <div>
                  <Label className="text-xs" htmlFor="settlement-date-input">Settlement Date <span className="text-[#B91C1C]">*</span></Label>
                  <Input
                    id="settlement-date-input"
                    type="date"
                    value={settlementDate}
                    onChange={(e) => setSettlementDate(e.target.value)}
                    className="h-9 mt-1"
                    data-testid="settlement-date-input"
                    required
                  />
                  <div className="text-[11px] text-[#6B7280] mt-1">Actual business date the case settled. Separate from the status-change timestamp.</div>
                </div>
                <div>
                  <Label className="text-xs">Settled amount</Label>
                  <Input type="number" placeholder={fmtMoney(loan.current_payoff)} value={settled} onChange={(e) => setSettled(e.target.value)} className="h-9 mt-1" data-testid="settled-amount-input" />
                  <div className="text-[11px] text-[#6B7280] mt-1">Leave blank to default to current payoff.</div>
                </div>
              </div>
            )}
            <div className="mb-3 space-y-1.5 border-t border-[#F3F4F6] pt-3">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">Send notifications</div>
              <label className="flex items-center gap-2 text-xs text-[#374151]">
                <input type="checkbox" checked={notifyPlaintiff} onChange={e => setNotifyPlaintiff(e.target.checked)} data-testid="notify-plaintiff-check" />
                Notify plaintiff
              </label>
              <label className="flex items-center gap-2 text-xs text-[#374151]">
                <input type="checkbox" checked={notifyAttorney} onChange={e => setNotifyAttorney(e.target.checked)} data-testid="notify-attorney-check" />
                Notify attorney
              </label>
              <label className="flex items-center gap-2 text-xs text-[#374151]">
                <input type="checkbox" checked={notifyInternal} onChange={e => setNotifyInternal(e.target.checked)} data-testid="notify-internal-check" />
                Notify internal team
              </label>
            </div>
            <Button onClick={saveStatus} className="w-full bg-[#0066FF] hover:bg-[#0052CC] h-9" data-testid="save-status-btn">
              <Save className="w-3.5 h-3.5 mr-1.5" /> Save status
            </Button>
            <Button onClick={() => setShowCustomEmail(true)} variant="outline" className="w-full mt-2 h-9" data-testid="send-custom-email-btn">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Send custom email
            </Button>
          </div>
          <LoanActionRail loan={loan} onSent={load} />
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Key info</div>
            <Stat label="Created by" value={loan.created_by || "—"} small />
            <div className="my-2 border-t border-[#F3F4F6]" />
            <Stat label="Created" value={formatDate(loan.created_at)} small />
            <div className="my-2 border-t border-[#F3F4F6]" />
            <Stat label="Last update" value={formatDate(loan.updated_at)} small />
            <div className="my-2 border-t border-[#F3F4F6]" />
            <Stat label="Source" value={loan.source} small />
          </div>
        </aside>
      </div>

      <GeneratePayoffModal open={showPayoff} onClose={() => setShowPayoff(false)} prefill={{ loanIds: [id] }} />
      <CreateTaskModal open={showTask} onClose={() => setShowTask(false)} prefill={{ related_loan_id: id, related_client_name: loan?.client_name }} />
      {showEdit && <EditLoanModal loan={loan} onClose={() => setShowEdit(false)} onSaved={load} />}
      {showEditSettlementDate && (
        <EditSettlementDateModal
          loan={loan}
          initialDate={settlementDate}
          onClose={() => setShowEditSettlementDate(false)}
          onSaved={() => { setShowEditSettlementDate(false); load(); }}
        />
      )}
      <SendCustomEmailDialog
        open={showCustomEmail}
        onClose={() => setShowCustomEmail(false)}
        loanId={loan?.loan_id}
        firmId={loan?.firm_id}
        defaultRecipient={loan?.attorney_email}
        defaultAttorneyName={loan?.attorney_name}
        onSent={load}
      />
    </div>
  );
}

function Stat({ label, value, accent, small }) {
  return (
    <div>
      <div className={`text-[11px] uppercase tracking-wider text-[#6B7280] ${small ? "" : "mb-1"}`}>{label}</div>
      <div className={`tabular-nums ${accent ? "text-[#0066FF] font-semibold font-display text-xl" : "text-[#0A2540] font-medium " + (small ? "text-sm" : "text-lg")}`}>{value}</div>
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-0.5">{label}</div>
      <div className="text-sm text-[#0A2540]">{value}</div>
    </div>
  );
}

function EditSettlementDateModal({ loan, initialDate, onClose, onSaved }) {
  const [date, setDate] = useState(initialDate || (loan.settlement_date || "").slice(0, 10));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Settlement date is required"); return; }
    if (date === (loan.settlement_date || "").slice(0, 10)) {
      toast.error("New date is the same as the current date");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/operations/loans/${loan.loan_id}/settlement-date`, {
        settlement_date: date,
        reason: reason.trim() || null,
      });
      toast.success("Settlement date updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0A2540]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-settlement-date-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-[#0066FF] font-medium mb-1">Edit Settlement Date</div>
          <div className="font-display text-2xl font-semibold text-[#0A2540]">{loan.client_name}</div>
          <div className="text-sm text-[#6B7280] mt-1">Current: <span className="tabular-nums font-medium text-[#0A2540]">{loan.settlement_date ? formatDate(loan.settlement_date) : "—"}</span></div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs" htmlFor="esd-date">New Settlement Date <span className="text-[#B91C1C]">*</span></Label>
            <Input id="esd-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required data-testid="esd-date-input" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs" htmlFor="esd-reason">Reason (recorded in audit log)</Label>
            <Textarea id="esd-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Attorney corrected the actual settlement date" data-testid="esd-reason-input" className="mt-1" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1" data-testid="esd-cancel-btn">Cancel</Button>
          <Button type="submit" disabled={saving} className="flex-1 bg-[#0066FF] hover:bg-[#0052CC]" data-testid="esd-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
}
